import React, { Component, Suspense, startTransition } from 'react';

// Error boundary for handling lazy loading errors
class LazyLoadErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Lazy loading error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center p-8">
            <div className="text-6xl mb-4">⚠️</div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Something went wrong
            </h2>
            <p className="text-gray-600 mb-4">
              Failed to load the page. Please try again.
            </p>
            <button
              onClick={() => this.setState({ hasError: false, error: null })}
              className="bg-rose-600 text-white px-4 py-2 rounded-lg hover:bg-rose-700"
            >
              Try Again
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Enhanced loading component
const OptimizedLoadingFallback = ({ message = "Loading page..." }) => (
  <div className="flex items-center justify-center min-h-screen bg-gray-50">
    <div className="text-center">
      <div className="loading-spinner w-8 h-8 mx-auto mb-4"></div>
      <p className="text-gray-600 animate-pulse">{message}</p>
    </div>
  </div>
);

// Navigation wrapper with proper lazy loading
export const NavigationWrapper = ({ children, currentPage = "unknown" }) => {
  return (
    <LazyLoadErrorBoundary>
      <Suspense 
        fallback={
          <OptimizedLoadingFallback 
            message={`Loading ${currentPage.replace('-', ' ')}...`} 
          />
        }
      >
        {children}
      </Suspense>
    </LazyLoadErrorBoundary>
  );
};

// Hook for handling navigation with startTransition
export const useOptimizedNavigation = (setCurrentPage, setPageData) => {
  const navigateWithTransition = (page, data = null) => {
    console.log(`🚀 Navigating to: ${page}`, data);
    
    // Use startTransition for non-blocking navigation
    startTransition(() => {
      setCurrentPage(page);
      if (data) {
        setPageData(data);
      }
    });
  };

  return navigateWithTransition;
};

// Component for handling deferred loading
export const DeferredPageLoader = ({ 
  pageComponent: PageComponent, 
  props = {},
  priority = false 
}) => {
  if (priority) {
    // High priority pages load immediately
    return <PageComponent {...props} />;
  }

  // Low priority pages are deferred
  return (
    <Suspense fallback={<OptimizedLoadingFallback />}>
      <PageComponent {...props} />
    </Suspense>
  );
};

// Preloader for critical pages
export const preloadCriticalPages = () => {
  // Preload most commonly used pages
  const criticalPages = [
    () => import('../pages/HomePage'),
    () => import('../pages/services/categories/WeddingServicesPageModular'),
    () => import('../pages/booking/BookingPage'),
  ];

  criticalPages.forEach(importFn => {
    try {
      importFn();
    } catch (error) {
      console.warn('Failed to preload page:', error);
    }
  });
};

const navigationUtils = {
  NavigationWrapper,
  useOptimizedNavigation,
  DeferredPageLoader,
  preloadCriticalPages,
  OptimizedLoadingFallback,
  LazyLoadErrorBoundary
};

export default navigationUtils;
